import React, { useState } from 'react';
import Icon from 'components/AppIcon';
import { ACCESS_LEVELS, FILE_TYPE_CONFIG } from './dataRoomData';

export default function DocumentCard({ doc, userRole, onPreview, onDownload, onViewHistory, onManageAccess, onDelete }) {
  const [menuOpen, setMenuOpen] = useState(false);
  const fileConfig = FILE_TYPE_CONFIG?.[doc?.fileType] || FILE_TYPE_CONFIG?.pdf;
  const accessConfig = ACCESS_LEVELS?.[doc?.accessLevel] || ACCESS_LEVELS?.internal;

  const canManageAccess = userRole === 'admin';
  const canDelete = userRole === 'admin';
  const canUpload = userRole === 'admin' || userRole === 'sponsor';

  return (
    <div className="bg-card border border-border rounded-xl p-4 flex flex-col gap-3 hover:shadow-md transition-all duration-200 card-interactive group relative">
      {/* File Type Icon + Access Badge */}
      <div className="flex items-start justify-between">
        <div
          className="flex items-center justify-center w-10 h-10 rounded-lg flex-shrink-0"
          style={{ background: fileConfig?.bg }}
        >
          <Icon name={fileConfig?.icon} size={20} color={fileConfig?.color} strokeWidth={2} />
        </div>
        <span
          className="text-xs font-semibold px-2 py-0.5 rounded-full font-caption border"
          style={{ color: accessConfig?.color, background: accessConfig?.bg, borderColor: accessConfig?.border }}
        >
          {accessConfig?.label}
        </span>
      </div>

      {/* Document Name */}
      <button
        onClick={() => onPreview(doc)}
        className="text-left font-caption text-sm font-semibold text-foreground hover:text-primary transition-colors duration-200 leading-snug line-clamp-2"
      >
        {doc?.name}
      </button>

      {/* Category + Version */}
      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-xs px-2 py-0.5 rounded-md bg-muted text-muted-foreground font-caption capitalize">
          {doc?.category?.replace('-', ' ')}
        </span>
        <span className="text-xs px-2 py-0.5 rounded-md bg-primary/10 text-primary font-caption font-semibold">
          {doc?.version}
        </span>
        <span className="text-xs font-caption uppercase font-bold" style={{ color: fileConfig?.color }}>
          {doc?.fileType}
        </span>
      </div>

      {/* Meta */}
      <div className="flex flex-col gap-1">
        <div className="flex items-center gap-1.5 text-xs text-muted-foreground font-caption">
          <Icon name="Calendar" size={12} />
          <span>{doc?.modifiedDate}</span>
        </div>
        <div className="flex items-center gap-1.5 text-xs text-muted-foreground font-caption">
          <Icon name="User" size={12} />
          <span>{doc?.modifiedBy}</span>
        </div>
        <div className="flex items-center gap-1.5 text-xs text-muted-foreground font-caption">
          <Icon name="HardDrive" size={12} />
          <span>{doc?.fileSize}</span>
        </div>
      </div>

      {/* Actions */}
      <div className="flex items-center gap-1.5 pt-1 border-t border-border">
        <button
          onClick={() => onPreview(doc)}
          className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-xs font-caption font-medium text-muted-foreground hover:text-primary hover:bg-primary/10 transition-all duration-200"
          title="Preview"
        >
          <Icon name="Eye" size={13} />
          <span>Preview</span>
        </button>
        <button
          onClick={() => onDownload(doc)}
          className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-xs font-caption font-medium text-muted-foreground hover:text-primary hover:bg-primary/10 transition-all duration-200"
          title="Download"
        >
          <Icon name="Download" size={13} />
          <span>Download</span>
        </button>
        <div className="ml-auto relative">
          <button
            onClick={() => setMenuOpen((p) => !p)}
            className="flex items-center justify-center w-7 h-7 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted transition-all duration-200"
            title="More options"
          >
            <Icon name="MoreHorizontal" size={15} />
          </button>
          {menuOpen && (
            <div
              className="absolute right-0 top-full mt-1 w-44 bg-popover border border-border rounded-lg shadow-lg py-1 z-50"
              onMouseLeave={() => setMenuOpen(false)}
            >
              <button
                onClick={() => { onViewHistory(doc); setMenuOpen(false); }}
                className="w-full flex items-center gap-2.5 px-3 py-2 text-sm font-caption text-foreground hover:bg-muted transition-colors duration-150"
              >
                <Icon name="History" size={14} /> View History
              </button>
              {canManageAccess && (
                <button
                  onClick={() => { onManageAccess(doc); setMenuOpen(false); }}
                  className="w-full flex items-center gap-2.5 px-3 py-2 text-sm font-caption text-foreground hover:bg-muted transition-colors duration-150"
                >
                  <Icon name="Users" size={14} /> Manage Access
                </button>
              )}
              <button
                className="w-full flex items-center gap-2.5 px-3 py-2 text-sm font-caption text-foreground hover:bg-muted transition-colors duration-150"
                onClick={() => setMenuOpen(false)}
              >
                <Icon name="Pencil" size={14} /> Rename
              </button>
              {canDelete && (
                <>
                  <div className="my-1 border-t border-border" />
                  <button
                    onClick={() => { onDelete(doc); setMenuOpen(false); }}
                    className="w-full flex items-center gap-2.5 px-3 py-2 text-sm font-caption text-error hover:bg-error/10 transition-colors duration-150"
                  >
                    <Icon name="Trash2" size={14} /> Delete
                  </button>
                </>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
